import React, { FunctionComponent, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@material-ui/core';
import { IImage } from '../../model/ICarDetails';
import CarUnavailable from '../../assets/images/carUnavailable.png';
import Slider, { Settings } from 'react-slick';
import VariantNextSliderButton from './variantSliderControls/VariantNextSliderButton';
import VariantPrevSliderButton from './variantSliderControls/VariantPrevSliderButton';
import variantSliderStyles from './variantSliderStyles';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import SliderDots from '../../../customise/components/customiseContainer/customiseDisplay/variantSlider/sliderDots/SliderDots';
import SliderImage from '../../../customise/components/customiseContainer/customiseDisplay/variantSlider/sliderImage/SliderImage';
import SectionStatesWrapper from '../configuratorStates/SectionStatesWrapper';
import OverlayLoader from '../../../customise/components/presentational/loaders/OverlayLoader';
import usePlatformConfig from '../../react/usePlatformConfig';
import useIsTablet from '../../react/useIsTablet';
import { TSectionsKeys } from '../../../customise/model/IStoreTypes';
import { CustomiseSections } from '../../enum/CustomiseSections';
import scrollInView, { ScrollInViewType } from '../../service/scrollInView';
import clsx from 'clsx';
import { ImageType } from '../../../gqlCommon/enum/ImageType';

export interface IVariantSlider {
  images: IImage[];
  loading: boolean;
  error: Error;
  alt?: string;
  titleComponent: ReactElement;
  selectedSection?: TSectionsKeys | string;
  currentIndex?: number;
  isInteriorImageDisplay?: boolean;
  setCurrentIndex?: (newIndex: number) => void;
  interiorImageType?: string;
  selectedSectionImagePov?: string;
}

const VariantSlider: FunctionComponent<IVariantSlider> = ({
  images,
  error,
  loading,
  alt,
  titleComponent,
  selectedSection,
  selectedSectionImagePov,
  interiorImageType = '',
}) => {
  const style = variantSliderStyles();
  const sliderRef = useRef(null);
  const [areDotsDisabled] = useState<boolean>(false);
  const [isUpdateFromSliderControls, setIsUpdateFromSliderControls] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isInteriorImageDisplay, setIsInteriorImageDisplay] = useState(false);
  const [hasInteriorImage] = useState(images.some(image => image.type === ImageType.INTERIOR && !!image.url));
  const sliderContainer = document.getElementById('variant_slider');
  const isTablet = useIsTablet();
  const {
    platformConfig: { isComplexModel },
  } = usePlatformConfig();
  const isSliderInView =
    sliderContainer &&
    scrollInView.isInView(sliderContainer, isTablet ? ScrollInViewType.BOTTOM : ScrollInViewType.TOP);

  const arrowStyles: { [key: string]: string } = {
    default: clsx(style.verticalOffset, { [style.interiorViewArrow]: isInteriorImageDisplay }),
    leftArrow: style.leftArrow,
    rightArrow: style.rightArrow,
  };

  const updateSlider = useCallback(
    newIndex => {
      sliderRef.current?.slickGoTo(newIndex);

      return setCurrentIndex(newIndex);
    },
    [sliderRef.current, currentIndex],
  );

  const sliderSettings: Partial<Settings> = {
    dots: true,
    appendDots: () => (
      <SliderDots
        items={images}
        areDotsDisabled={areDotsDisabled}
        currentSelection={currentIndex}
        makeSelection={event => {
          updateSlider(+(event.target as any).value);
          setIsUpdateFromSliderControls(true);
        }}
        isInteriorImageDisplay={isInteriorImageDisplay}
      />
    ),
    beforeChange: (_oldIndex: number, newIndex: number) => updateSlider(newIndex),
    infinite: false,
    arrows: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    className: style.root,
    prevArrow: (
      <VariantPrevSliderButton
        customStyles={clsx(arrowStyles.default, arrowStyles.leftArrow)}
        isInteriorImage={isInteriorImageDisplay}
        setIsUpdateFromSliderControls={setIsUpdateFromSliderControls}
      />
    ),
    nextArrow: (
      <VariantNextSliderButton
        customStyles={clsx(arrowStyles.default, arrowStyles.rightArrow)}
        isInteriorImage={isInteriorImageDisplay}
        setIsUpdateFromSliderControls={setIsUpdateFromSliderControls}
      />
    ),
    adaptiveHeight: true,
  };

  useEffect(() => {
    setIsUpdateFromSliderControls(false);
  }, [selectedSection]);

  useEffect(() => {
    if (hasInteriorImage && isSliderInView) {
      setIsInteriorImageDisplay(images[currentIndex]?.type === interiorImageType);
    }
  }, [images, currentIndex, isSliderInView, hasInteriorImage]);

  useEffect(() => {
    if (!isComplexModel) {
      let newImageIndex = 0;

      if (!hasInteriorImage || !isSliderInView || isUpdateFromSliderControls) {
        return;
      }
      if (selectedSection === CustomiseSections.INTERIOR) {
        const foundIndex = images.findIndex(image => image.type === ImageType.INTERIOR);
        newImageIndex = foundIndex !== -1 ? foundIndex : images.length - 1;
      }

      updateSlider(newImageIndex);
    }
  }, [hasInteriorImage, selectedSection, selectedSectionImagePov, isSliderInView, isUpdateFromSliderControls]);

  useEffect(() => {
    if (isComplexModel && !isUpdateFromSliderControls && !loading) {
      const newImageIndex = images.findIndex(img => img.type === selectedSectionImagePov);
      updateSlider(newImageIndex !== -1 ? newImageIndex : 0);
    }
  }, [isComplexModel, selectedSectionImagePov, isUpdateFromSliderControls, loading]);

  return (
    <Box>
      {titleComponent}
      <Box id="variant_slider">
        <SectionStatesWrapper error={error}>
          <OverlayLoader loading={loading} width={'100%'}>
            <Slider ref={sliderRef} key={'variantSlider'} {...sliderSettings}>
              {images.length ? (
                images.map((image: IImage, index: number) => (
                  <SliderImage
                    image={image}
                    index={index}
                    key={`index_${index}`}
                    alt={alt}
                    defaultSrc={CarUnavailable}
                  />
                ))
              ) : (
                <SliderImage defaultSrc={CarUnavailable} />
              )}
            </Slider>
          </OverlayLoader>
        </SectionStatesWrapper>
      </Box>
    </Box>
  );
};

export default VariantSlider;
